import { createContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import axios from 'axios';
import { ENV } from '../../config/environment';
import { ServiceName, ServiceStatusState, LastCheckedState, ServiceStatusContextType } from './types';

const SERVICE_URLS: Record<ServiceName, string> = {
  auth: ENV.AUTH_SERVICE_URL.replace('/api/auth', ''),
  user: ENV.USER_SERVICE_URL,
  registration: ENV.REGISTRATION_SERVICE_URL,
  inspection: ENV.INSPECTION_SERVICE_URL,
  checklist: ENV.CHECKLIST_SERVICE_URL,
  notification: ENV.NOTIFICATION_SERVICE_URL,
  scraper: ENV.SCRAPER_SERVICE_URL || 'http://localhost:8003',
};

const POLLING_INTERVAL = 100000;
const HEALTH_CHECK_TIMEOUT = 5000;

const initialStatus: ServiceStatusState = {
  auth: 'checking',
  user: 'checking',
  registration: 'checking',
  inspection: 'checking',
  checklist: 'checking',
  notification: 'checking',
  scraper: 'checking',
};

const initialLastChecked: LastCheckedState = {
  auth: null,
  user: null,
  registration: null,
  inspection: null,
  checklist: null,
  notification: null,
  scraper: null,
};

export const ServiceStatusContext = createContext<ServiceStatusContextType | null>(null);

export function ServiceStatusProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<ServiceStatusState>(initialStatus);
  const [lastChecked, setLastChecked] = useState<LastCheckedState>(initialLastChecked);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  const checkService = useCallback(async (service: ServiceName): Promise<void> => {
    try {
      const url = `${SERVICE_URLS[service]}/health`;
      const response = await axios.get(url, { timeout: HEALTH_CHECK_TIMEOUT });

      if (!isMountedRef.current) return;

      let isHealthy = false;
      if (response.status === 200) {
        if (typeof response.data === 'string') {
          isHealthy = response.data.toLowerCase().includes('healthy');
        } else if (response.data && typeof response.data === 'object') {
          isHealthy = response.data.status?.toLowerCase() === 'healthy';
        }
      }

      setStatus(prev => ({ ...prev, [service]: isHealthy ? 'online' : 'offline' }));
    } catch {
      if (!isMountedRef.current) return;
      setStatus(prev => ({ ...prev, [service]: 'offline' }));
    } finally {
      if (isMountedRef.current) {
        setLastChecked(prev => ({ ...prev, [service]: new Date() }));
      }
    }
  }, []);

  const checkAllServices = useCallback(async (): Promise<void> => {
    const services: ServiceName[] = ['auth', 'user', 'registration', 'inspection', 'checklist', 'notification', 'scraper'];
    await Promise.all(services.map(service => checkService(service)));
  }, [checkService]);

  useEffect(() => {
    isMountedRef.current = true;
    checkAllServices();

    pollingRef.current = setInterval(() => {
      checkAllServices();
    }, POLLING_INTERVAL);

    return () => {
      isMountedRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [checkAllServices]);

  const getServiceStatus = useCallback((service: ServiceName) => status[service], [status]);
  const isServiceOnline = useCallback((service: ServiceName) => status[service] === 'online', [status]);
  const isServiceOffline = useCallback((service: ServiceName) => status[service] === 'offline', [status]);

  const isSystemOffline = status.auth === 'offline';
  const offlineServices = (Object.keys(status) as ServiceName[]).filter(
    service => status[service] === 'offline'
  );

  const value: ServiceStatusContextType = {
    status,
    lastChecked,
    isSystemOffline,
    checkService,
    checkAllServices,
    getServiceStatus,
    isServiceOnline,
    isServiceOffline,
    offlineServices,
  };

  return (
    <ServiceStatusContext.Provider value={value}>
      {children}
    </ServiceStatusContext.Provider>
  );
}
